/**
 * Which blanks of the 203 the Incident Roster fills in, and how it matches
 * them.
 *
 * The card is documentation only: it reads the roster but never writes back to
 * it, which is what the header tooltip warns about. These seats are therefore
 * shown read-only, because typing over them would be lost on the next refetch
 * and would disagree with the roster the PDF export reads.
 */

import type { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';

export const ROSTER_READONLY_SEATS = new Set<string>([
  'Incident Commander',
  'Incident Commander 2',
  'Incident Commander 3',
  'Incident Commander 4',
  'Incident Commander 5',
  'Safety Officer',
  'Public Information Officer',
  'Liaison Officer',
  'Planning Section Chief',
  'Resource Unit Leader',
  'Situation Unit Leader',
  'Environmental Unit Leader',
]);

/**
 * Seats the roster and the printed form disagree on the name of.
 *
 * The 203-CG prints "Information Officer" and "Resources Unit", while the
 * roster stores the NIMS wording, so a match on the canonical name alone would
 * report an assigned seat as vacant.
 */
const SEAT_MATCH_ALIASES: Record<string, string[]> = {
  'Public Information Officer': ['Information Officer'],
  'Resource Unit Leader': ['Resources Unit Leader'],
};

export function seatMatches(
  memberSeat: string[] | null | undefined,
  canonicalSeat: string
): boolean {
  if (!memberSeat) return false;
  const m = memberSeat.map((s) => s.toLowerCase());
  if (m.includes(canonicalSeat.toLowerCase())) return true;
  const aliases = SEAT_MATCH_ALIASES[canonicalSeat];
  return aliases?.some((a) => m.includes(a.toLowerCase())) ?? false;
}

/**
 * Everyone holding a seat, as one line.
 *
 * A seat can be held by more than one person, and the printed box is one line,
 * so they are joined. `isAssigned` is what the commander table filters on, so
 * only seats someone actually holds become rows.
 */
export function getRosterAssigneesDisplay(
  rosterData: RosterEntry[] | undefined,
  canonicalSeat: string
): { display: string; isAssigned: boolean } {
  if (!rosterData?.length) {
    return { display: 'Not Assigned', isAssigned: false };
  }
  const labels = rosterData
    .filter((member) =>
      seatMatches(
        member.incidentPositions.map((p) => p.positionName),
        canonicalSeat
      )
    )
    .map((m) => m.userName || m.emailAddress || 'Unknown')
    .filter(Boolean);
  const unique = [...new Set(labels)];
  if (unique.length === 0) {
    return { display: 'Not Assigned', isAssigned: false };
  }
  return { display: unique.join(', '), isAssigned: true };
}

/** The five commander rows box 4 can print, in printed order. */
export const INCIDENT_COMMANDER_SEATS = [
  'Incident Commander',
  'Incident Commander 2',
  'Incident Commander 3',
  'Incident Commander 4',
  'Incident Commander 5',
] as const;
